// Core HTTP helper + per-service base URLs. Endpoint modules in this folder
// (strategies.ts / candles.ts / backtests.ts / etc.) build on top of `fetchJson`.
//
// Backend URLs are protocol-aware so the testing UI works under both launch profiles:
// - "http" profile (each service on its plain :5xxx port) — UI at http://localhost:5180
//   calls http://localhost:5xxx/...
// - "https" profile (VS Multiple-Startup-Projects default) — services bind both ports;
//   UI at https://localhost:7180 must call https://localhost:7xxx/... to avoid mixed
//   content and UseHttpsRedirection 307s.
//
// `launchSettings.json` port mapping:
//   TestUi               7180  ←→  5180
//   LiveCandles          7280  ←→  5171
//   HistoricalCandles    7133  ←→  5216
//   HistoricalIndicators 7065  ←→  5072
//   LiveIndicators       7213  ←→  5169
//   Orchestrator         7293  ←→  5123

// In PROD the Vite build pipeline sets every VITE_* below to "" (empty string) so the
// SPA hits same-origin paths like "/Backtests/run" — the host nginx path-routes them to
// the right backend service. In LOCAL DEV the VITE_* vars are unset, so we fall back
// to the launchSettings localhost:port mapping (works under both http and https profiles).
function envUrl(key: string, devFallback: string): string {
  const v = (import.meta.env as Record<string, unknown>)[key];
  return typeof v === "string" ? v : devFallback;
}

const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";

const HTTP_PORTS = {
  testUi: 5180,
  liveCandles: 5171,
  historicalCandles: 5216,
  historicalIndicators: 5072,
  liveIndicators: 5169,
  orchestrator: 5123,
} as const;

const HTTPS_PORTS = {
  testUi: 7180,
  liveCandles: 7280,
  historicalCandles: 7133,
  historicalIndicators: 7065,
  liveIndicators: 7213,
  orchestrator: 7293,
} as const;

const ports = isHttps ? HTTPS_PORTS : HTTP_PORTS;
const scheme = isHttps ? "https" : "http";

export const URLS = {
  testUi:               envUrl("VITE_TESTUI",                `${scheme}://localhost:${ports.testUi}`),
  liveCandles:          envUrl("VITE_LIVE_CANDLES",          `${scheme}://localhost:${ports.liveCandles}`),
  historicalCandles:    envUrl("VITE_HISTORICAL_CANDLES",    `${scheme}://localhost:${ports.historicalCandles}`),
  historicalIndicators: envUrl("VITE_HISTORICAL_INDICATORS", `${scheme}://localhost:${ports.historicalIndicators}`),
  liveIndicators:       envUrl("VITE_LIVE_INDICATORS",       `${scheme}://localhost:${ports.liveIndicators}`),
  orchestrator:         envUrl("VITE_ORCHESTRATOR",          `${scheme}://localhost:${ports.orchestrator}`),
} as const;

const API_KEY_STORAGE_KEY = "traderx.testui.apiKey";

export function getStoredApiKey(): string {
  return localStorage.getItem(API_KEY_STORAGE_KEY) ?? "";
}
export function setStoredApiKey(key: string) {
  localStorage.setItem(API_KEY_STORAGE_KEY, key);
}

export interface FetchJsonOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  apiKey?: string;
  signal?: AbortSignal;
}

/**
 * Centralized fetch wrapper. Injects X-Api-Key from localStorage, surfaces RFC 7807
 * ProblemDetails responses as Error objects with `.message` populated. Returns
 * `undefined` for 204 No Content responses. Errors throw with a useful message for the
 * UI to render.
 */
export async function fetchJson<T>(url: string, opts: FetchJsonOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  const apiKey = opts.apiKey ?? getStoredApiKey();
  if (apiKey) headers["X-Api-Key"] = apiKey;

  const response = await fetch(url, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    // CORS with credentials is set up server-side; the UI itself doesn't need to send any.
    credentials: "omit",
  });

  if (!response.ok) {
    let detail = `HTTP ${response.status} ${response.statusText}`;
    try {
      const problem = await response.json();
      if (problem?.title) detail = problem.detail ? `${problem.title}: ${problem.detail}` : problem.title;
    } catch {
      // body wasn't JSON — keep the status text fallback
    }
    throw new Error(detail);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
